import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormFields, LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { formatText } from "@ui/format";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import type { ProgressButtonState } from "bm-ui-kit";
import dayjs from "dayjs";
import React from "react";
import type { ReactNode } from "react";
import type { SmeLoanLeadCalcRequest, SmeLoanLeadCalcResponse } from "../LeadForm/types";
import * as Step from "./Steps";
import { COMPANY_LEGAL_FORM_OPTIONS, NUMBER_OF_CLOSED_YEARS_OPTIONS } from "./Steps";
import { SZECHENYI_LOAN_GOALS } from "./Steps/LoanGoalStep";
import { COMPANY_KOS, ENTERPRISE_FORMS, ENTERPRISE_KOS } from "./data";
import { SZECHENYI_LOAN_NAMES, calculateSzechenyiLoans } from "./domain";
import type { SzechenyiLeadRequest, SzechenyiLoanType } from "./types";
import "./style.scss";

interface FormFields extends SzechenyiLeadRequest, AbstractLeadFormFields {}

interface State<TFormFields extends FormFields = FormFields> extends AbstractLeadFormState<TFormFields> {
	availableLoans: Record<string, number>;
	koMessage: string;
	submitState: ProgressButtonState;
}

/**
 * @Shortcode
 *
 * @title "Új Széchenyi hitelprogram"
 * @shortcode "new_szechenyi_loans_small"
 * @slug "uj-szechenyi-hitelprogram"
 * @type "calculator"
 */
export class SmeLoanSzechenyiLoanSmallCalculator extends AbstractLeadForm<
	SmeLoanLeadCalcRequest,
	SmeLoanLeadCalcResponse,
	FormFields,
	AbstractLeadFormProps<SmeLoanLeadCalcRequest, SmeLoanLeadCalcResponse, any>,
	State
> {
	// EventParams
	protected readonly productTypes: string = "SME_LOAN";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		outsideTitle: "Széchenyi hitel kalkulátor",
		outsideTitleLink: "/vallalati/hitel/szechenyi-hitel/",
		bottomLogo: true,
		title: "Széchenyi hitel kalkulátor",
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			koMessage: "",
			availableLoans: {},
			submitState: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const {
			values: { companySector, companyFoundYear, email },
			submitState,
			koMessage,
			availableLoans,
		} = this.state;
		const isAgrar = companySector === "AGRAR";

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" />
				<Step.Step2 stepName="step-2" defaultTarget="step-3" />
				<Step.CompanyStep1
					stepName="company-1"
					defaultTarget="company-2"
					isAgrar={isAgrar}
					companyFoundYear={companyFoundYear}
					canStepBack
				/>
				<Step.CompanyStep2 stepName="company-2" defaultTarget="company-3" canStepBack />
				<Step.CompanyStep3 stepName="company-3" defaultTarget="company-4" canStepBack />
				<Step.CompanyStep4 stepName="company-4" defaultTarget="loan-goal" canStepBack />
				<Step.EnterpriseStep1
					stepName="enterprise-1"
					defaultTarget="enterprise-2"
					companyFoundYear={companyFoundYear}
					canStepBack
				/>
				<Step.EnterpriseStep2 stepName="enterprise-2" defaultTarget="enterprise-3" canStepBack />
				<Step.EnterpriseStep3 stepName="enterprise-3" defaultTarget="loan-goal" canStepBack />
				<Step.LoanGoalStep stepName="loan-goal" defaultTarget="result" canStepBack />
				<Step.ResultKoStep stepName="result-ko" message={koMessage} canStepBack />
				<Step.ResultSuccess
					stepName="result-success"
					defaultTarget="lead-form"
					availableLoans={Object.keys(availableLoans) as SzechenyiLoanType[]}
				/>
				<Step.LeadForm stepName="lead-form" defaultTarget="success-lead" submitState={submitState} />
				<Step.SuccessPage
					stepName="success-lead"
					subscriptionTypes={["CORPORATE_NEWSLETTER"]}
					userEmail={email}
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "step-3":
				return this.onStep3();
			case "loan-goal":
				return this.checkKo(target);
			case "result":
				return this.onResultStep(target);
			case "success-lead":
				this.setState({
					submitState: "loading",
				});
				this.sendLead()
					.then(() => {
						super.onStep(target, false);
						this.setState({
							submitState: "done",
						});
					})
					.catch((error: Error) => {
						this.setState({
							submitState: "error",
						});

						logError(error);
					});
				break;
			default:
				return super.onStep(target, withEvent);
		}
	}

	private onStep3(): void {
		const {
			values: { companySector, companyFoundYear, companyLegalForm },
		} = this.state;

		if (companyFoundYear >= dayjs().subtract(1, "year").year()) {
			this.setState({
				koMessage: "Sajnáljuk, finanszírozás nem nyújtható!",
			});

			return super.onStep("result-ko", true);
		}

		if (companySector === undefined) {
			this.setState({
				koMessage: "Finanszírozás nem nyújtható!",
			});

			return super.onStep("result-ko", true);
		}

		if (ENTERPRISE_FORMS.includes(companyLegalForm)) {
			return super.onStep("enterprise-1", true);
		}

		return super.onStep("company-1", true);
	}

	private checkKo(target: string): void {
		const {
			values,
			values: { companyLegalForm },
			previousSteps,
		} = this.state;

		if (previousSteps[previousSteps.length - 1] === target) {
			// This is a step back, just call super
			super.onStep(target, true);

			return;
		}

		const errors: string[] = [];

		if (ENTERPRISE_FORMS.includes(companyLegalForm)) {
			Object.keys(ENTERPRISE_KOS).forEach((index) => {
				if (values[index]) {
					errors.push(ENTERPRISE_KOS[index]);
				}
			});
		} else {
			Object.keys(COMPANY_KOS).forEach((index) => {
				if (values[index]) {
					errors.push(COMPANY_KOS[index]);
				}
			});
		}

		if (errors.length > 0) {
			this.setState(
				{
					koMessage: errors[0],
				},
				() => {
					super.onStep("result-ko", true);
				},
			);
		} else {
			super.onStep(target, true);
		}
	}

	private getResults(values: FormFields) {
		this.promisePool
			.add(calculateSzechenyiLoans(values))
			.then((response) => {
				this.setState({
					availableLoans: response?.maxLoanAmounts,
				});

				if (Object.keys(response?.maxLoanAmounts).length) {
					super.onStep("result-success", true);
				} else {
					this.setState(
						{
							koMessage: "Finanszírozás nem nyújtható!",
						},
						() => {
							super.onStep("result-ko", true);
						},
					);
				}
			})
			.catch((e) => {
				console.error("Calc endpoint returned error: ", e);
			});
	}

	private onResultStep(_target: string): void {
		const { values } = this.state;

		this.getResults(values);

		this.sendCalculationEvent();
	}

	protected getLeadData(): LeadRequest<SmeLoanLeadCalcRequest, SmeLoanLeadCalcResponse> {
		const {
			values: {
				netIncome,
				companyFoundYear,
				companyLegalForm,
				loanAmount,
				companyMainActivity,
				loanGoal,
				companyName,
				taxBaseRevenue,
				taxNumber,
				numberOfClosedYears,
				cityId,
				negativeLastClosedBusinessYear,
				negativeEquity,
				hasOverdueNavDebt,
				hasOverdueLoanDebt,
				hasAnotherCompanyUnderLiquidation,
				hasConnectedCompanyUnderLiquidation,
				ownerInKhr,
				ownerDenied,
				hadNavCollection,
				hasRescheduledPublicDebt,
				canOwnerOfferGuarantee,
				hadMoreThanHalfOwnerChange,
				hadMoreThanHalfPropertyOwnership,
				hasNegativeIncomeAfterTaxation,
				question,
			},
			availableLoans,
		} = this.state;
		const data = super.getLeadData();

		data.request = {
			...data.request,
			netIncome,
			loanAmount,
			companyLegalForm,
			companyMainActivity,
			companyFoundYear,
			companyTaxNumber: taxNumber,
			loanGoal,
			companyName,
			cityId,
			ownerInKhr,
			ownerHasAnotherCompanyUnderLiquidation: hasConnectedCompanyUnderLiquidation,
			positiveLastClosedBussinessYear: !negativeLastClosedBusinessYear,
			hasOverdueLoanDebt,
			hasOverdueNavDebt,
			question,
		};

		const isEnterprise = ENTERPRISE_FORMS.includes(companyLegalForm);

		// Választós
		const otherData = new JsonDataBuilder(data.request.otherDataJson);

		// Step 2 adatok
		otherData.addItem(
			"Társasági forma",
			"companyLegalForm",
			COMPANY_LEGAL_FORM_OPTIONS.find((option) => option.value === companyLegalForm).label,
		);
		otherData.addItem("Alapítás éve", "companyFoundYear", companyFoundYear);
		otherData.addItem("Fő tevékenység", "companyMainActivity", companyMainActivity);
		otherData.addItem("Cég Adószáma", "Cég Adószáma", taxNumber);
		otherData.addItem(
			"Lezárt pénzügyi (teljes) évek száma",
			"numberOfClosedYears",
			NUMBER_OF_CLOSED_YEARS_OPTIONS.find((option) => option.value === numberOfClosedYears).label,
		);

		// CompanyStep 1 adatok
		otherData.addItem("Árbevétel (lezárt év)", "netIncome", netIncome, "ft");

		// CompanyStep 2 adatok
		otherData.addItem(
			"Utolsó lezárt pénzügyi év üzemi/adózott eredménye negatív",
			"negativeLastClosedBusinessYear",
			negativeLastClosedBusinessYear,
			"bool",
		);

		otherData.addItem("Utolsó lezárt pénzügyi év saját tőkéje negatív", "negativeEquity", negativeEquity, "bool");
		otherData.addItem("Van lejárt NAV tartozása a cégednek", "hasOverdueNavDebt", hasOverdueNavDebt, "bool");
		otherData.addItem("Van a cégnek lejárt hiteltartozása", "hasOverdueLoanDebt", hasOverdueLoanDebt, "bool");
		otherData.addItem(
			"Van a tulajdonosi körben felszámolás alatt álló cég",
			"hasAnotherCompanyUnderLiquidation",
			hasAnotherCompanyUnderLiquidation,
			"bool",
		);

		// EnterpriseStep 2 adatok
		otherData.addItem(
			"A vállalkozás adózás utáni jövedelme negatív",
			"hasNegativeIncomeAfterTaxation",
			hasNegativeIncomeAfterTaxation,
			"bool",
		);

		// CompanyStep 3 adatok
		otherData.addItem(
			"Van a cég kapcsolt vállalkozásai között felszámolás alatt álló cég",
			"hasConnectedCompanyUnderLiquidation",
			hasConnectedCompanyUnderLiquidation,
			"bool",
		);
		otherData.addItem(
			isEnterprise
				? "Mint magánszemély, illetve a készfizető kezes szerepel a KHR/BAR listán"
				: "Tulajdonosok/ügyvezetők/magánszemély készfizető kezesek szerepelnek a KHR/BAR listán",
			"ownerInKhr",
			ownerInKhr,
			"bool",
		);
		otherData.addItem(
			"Tulajdonosi és ügyvezető körben van cégvezetéstől eltiltott személy",
			"ownerDenied",
			ownerDenied,
			"bool",
		);
		otherData.addItem(
			"Volt inkasszó a vállalkozás számláján az elmúlt 36 hónapban",
			"hadNavCollection",
			hadNavCollection,
			"bool",
		);

		// CompanyStep 4 adatok
		otherData.addItem(
			"Van a cégednek átütemezett köztartozása (pl. NAV részletfizetés)",
			"hasRescheduledPublicDebt",
			hasRescheduledPublicDebt,
			"bool",
		);
		otherData.addItem(
			"Tulajdonosi kezességvállalás biztosítékként felajánlható",
			"canOwnerOfferGuarantee",
			canOwnerOfferGuarantee,
			"bool",
		);
		otherData.addItem(
			"Tulajdonosok/kezesek tulajdonában van ingatlan",
			"hadMoreThanHalfPropertyOwnership",
			hadMoreThanHalfPropertyOwnership,
			"bool",
		);
		otherData.addItem(
			"Van 50%-nál nagyobb részben tulajdonos változás az elmúlt 6 hónapban",
			"hadMoreThanHalfOwnerChange",
			hadMoreThanHalfOwnerChange,
			"bool",
		);

		// EnterpriseStep 1 adatok
		otherData.addItem("Adóalapba számító bevétel (lezárt év)", "taxBaseRevenue", taxBaseRevenue, "ft");

		otherData.addItem(
			"Hitelcél",
			"loanGoal",
			SZECHENYI_LOAN_GOALS.find((option) => option.value === loanGoal)?.label,
		);
		otherData.addItem("Hitelösszeg", "loanAmount", loanAmount, "ft");

		data.response.availableLoans =
			Object.keys(availableLoans)
				.map((loan) => SZECHENYI_LOAN_NAMES[loan])
				.join(", ") || "Széchenyi hitelre nem alkalmas";
		data.response.productName = data.response.availableLoans;

		Object.entries(availableLoans).forEach(([loanId, maxLoanAmount]) => {
			otherData.addItem(
				`Elérhető hitelösszeg (${SZECHENYI_LOAN_NAMES[loanId]})`,
				SZECHENYI_LOAN_NAMES[loanId],
				maxLoanAmount,
				"ft",
			);
		});

		data.request.otherDataJson = otherData.getJson();

		return data;
	}

	protected getAutoresponderName(): string {
		const {
			values: { loanGoal, companySector },
		} = this.state;

		if (companySector === "EGYEB") {
			if (["LOAN_REFINANCING"].includes(loanGoal)) {
				return "autoresponder-szechenyi-hitelkivaltas";
			}

			if (["DAILY_OPERATION", "ACQUISITIONS_FINANCING"].includes(loanGoal)) {
				return "autoresponder-szechenyi-folyoeslikviditas";
			}

			return "autoresponder-szechenyi-beruhazasi";
		}

		if (companySector === "AGRAR") {
			if (["DAILY_OPERATION", "ACQUISITIONS_FINANCING"].includes(loanGoal)) {
				return "autoresponder-szechenyi-agrar";
			}

			return "autoresponder-szechenyi-agrar-beruhazasi";
		}

		if (companySector === "TURISZTIKA") {
			return "autoresponder-szechenyi-turisztikai";
		}

		return undefined;
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { firstName, lastName },
			availableLoans,
		} = this.state;

		const { consultantName, consultantEmail } = this.props;
		const maxLoanAmount = Object.values(availableLoans).reduce((max, current) => Math.max(max, current), 0);

		return {
			lastName,
			firstName,
			consultantEmail,
			consultantName,
			maxLoanAmount: formatText(maxLoanAmount, "ft"),
		};
	}

	protected getClassName(): string {
		return `${super.getClassName()} szechenyi-loan`;
	}

	protected getProductType(): ProductType {
		return "SME_LOAN";
	}

	protected getComponentName(): string {
		return "SmeLoanSzechenyiLoanSmallCalculator";
	}
}

export default SmeLoanSzechenyiLoanSmallCalculator;
